import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Tabs, Tab, Typography, Paper, Snackbar, Alert } from '@mui/material';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import BuildIcon from '@mui/icons-material/Build';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import HandymanIcon from '@mui/icons-material/Handyman';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';

import { CustomerSupportNewInstallation } from './CustomerSupportNewInstallation';
import { CustomerSupportComplaint } from './CustomerSupportComplain';
import { CustomerSupportRemovalTransfer } from './CustomerSupportRemovalTransfer';
import { CustomerSupportRepairReplacement } from './CustomerSupportRepairReplacement';

const TABS = [
  { label: 'New Installation', icon: <BuildIcon fontSize="small" />, component: CustomerSupportNewInstallation },
  { label: 'Complaints',       icon: <ReportProblemIcon fontSize="small" />, component: CustomerSupportComplaint },
  { label: 'Removal / Transfer', icon: <SwapHorizIcon fontSize="small" />, component: CustomerSupportRemovalTransfer },
  { label: 'Repair / Replacement', icon: <HandymanIcon fontSize="small" />, component: CustomerSupportRepairReplacement },
];

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

export default function CustomerSupport() {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = React.useState(location.state?.tab ?? 0);
  const [snackbar, setSnackbar] = React.useState({ open: false, message: '' });

  React.useEffect(() => {
    if (location.state?.successMessage) {
      setSnackbar({ open: true, message: location.state.successMessage });
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box sx={{ width: '100%', minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Page header */}
      <Box sx={{ px: 3, pt: 3, pb: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <SupportAgentIcon sx={{ fontSize: 32, color: 'primary.main' }} />
        <Typography variant="h5" fontWeight={600}>
          Customer Support
        </Typography>
      </Box>

      {/* Tab bar */}
      <Paper elevation={0} sx={{ mx: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            px: 1,
            '& .MuiTab-root': { minHeight: 52, textTransform: 'none', fontWeight: 500, fontSize: '0.9rem', gap: 0.75 },
            '& .Mui-selected': { fontWeight: 700 },
          }}
        >
          {TABS.map((tab, i) => (
            <Tab key={i} label={tab.label} icon={tab.icon} iconPosition="start" />
          ))}
        </Tabs>
      </Paper>

      {/* Tab content — each section is self-contained */}
      {TABS.map((tab, i) => (
        <TabPanel key={i} value={activeTab} index={i}>
          <tab.component />
        </TabPanel>
      ))}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))} severity="success" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
